import { useEffect, type FC } from "react";
import { useChatContext } from "duck/ui/chat/contexts/useChatContext";

import { actionHandlerRegistry } from "./index";
import type { ClientAction } from "./types";

// Action handlers are renderless React components rather than plain functions or classes.
// This lets each handler use hooks (useContext, useEffect, etc.) to access whatever it needs,
// making handlers self-sufficient without requiring the framework to pass an explicit context object
// that would need to grow every time a new handler type is added.
export const ClientActionDispatcher: FC<{ actions: ClientAction[] }> = ({
  actions,
}) => {
  const { setPendingClientActions } = useChatContext();

  // Drain processed actions so handler components unmount and don't accumulate.
  // This assumes (a) React fires child effects before parent effects within
  // the same commit, and (b) handler effects run synchronously — both hold
  // in practice but (a) is not a documented React API contract.
  useEffect(() => {
    if (actions.length > 0) {
      setPendingClientActions([]);
    }
  }, [actions, setPendingClientActions]);

  return (
    <>
      {actions.map((action, i) => {
        const handlers = actionHandlerRegistry.getHandlers(action.type);

        return handlers.map((Handler, j) => (
          <Handler key={`${action.type}-${i}-${j}`} action={action} />
        ));
      })}
    </>
  );
};
